import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SITE_URL, SITE_NAME, resolveSeo } from '@/lib/seo';
import { injectJsonLd, clearJsonLd } from '@/lib/jsonld';

function ensureMeta(attr: 'name' | 'property', key: string): HTMLMetaElement {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  return el;
}

function setMeta(attr: 'name' | 'property', key: string, content: string): void {
  ensureMeta(attr, key).setAttribute('content', content);
}

function removeMeta(attr: 'name' | 'property', key: string): void {
  document
    .head
    .querySelectorAll(`meta[${attr}="${key}"]`)
    .forEach((el) => el.remove());
}

function ensureCanonical(): HTMLLinkElement {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  return el;
}

export default function SeoManager() {
  const location = useLocation();

  useEffect(() => {
    const seo = resolveSeo(location.pathname);
    const canonicalUrl = seo.path === '/' ? `${SITE_URL}/` : `${SITE_URL}${seo.path}`;

    document.title = seo.title;

    setMeta('name', 'description', seo.description);
    setMeta('name', 'keywords', seo.keywords);

    if (seo.robots) {
      setMeta('name', 'robots', seo.robots);
    } else {
      removeMeta('name', 'robots');
    }

    ensureCanonical().setAttribute('href', canonicalUrl);

    setMeta('property', 'og:title', seo.title);
    setMeta('property', 'og:description', seo.description);
    setMeta('property', 'og:type', seo.ogType);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:site_name', SITE_NAME);

    setMeta('name', 'twitter:card', seo.ogImage ? 'summary_large_image' : 'summary');
    setMeta('name', 'twitter:title', seo.title);
    setMeta('name', 'twitter:description', seo.description);

    if (seo.ogImage) {
      setMeta('property', 'og:image', seo.ogImage);
      setMeta('name', 'twitter:image', seo.ogImage);
    } else {
      removeMeta('property', 'og:image');
      removeMeta('name', 'twitter:image');
    }

    if (seo.jsonLd.length > 0) {
      injectJsonLd(seo.jsonLd);
    } else {
      clearJsonLd();
    }
  }, [location.pathname]);

  return null;
}